import { Link } from "react-router"
import { useTranslation } from "react-i18next"

import type { CurrentSubscription } from "@/api/generated/model"
import { ProgressBar } from "@/components/kit/ProgressBar"
import { Button } from "@/components/ui/button"
import { formatDate } from "@/lib/datetime"
import { formatCount, localeOf } from "@/lib/format"
import { useOverviewAccess, useUsageCard } from "../api/overview"
import { Region } from "./Region"

/**
 * FR-DSH-OVW-02: today and this month against the plan's limits, the plan
 * and its renewal; a limit of −1 is "Unlimited" (DW-28). Buying more or
 * changing plan exists only in normal mode.
 */
export function UsageCard({ className }: { className?: string }) {
  const { t } = useTranslation("overview")
  const can = useOverviewAccess()
  const usage = useUsageCard(can.usage)
  if (!can.usage) return null

  return (
    <Region title={t("usage.title")} query={usage} className={className}>
      {() =>
        usage.data ? (
          <div className="space-y-4">
            <Usage current={usage.data} />
            {can.money ? (
              <div className="flex flex-wrap gap-2 border-t border-border pt-4">
                <Button asChild size="sm" variant="outline">
                  <Link to="/plans#overage">{t("usage.buyMore")}</Link>
                </Button>
                <Button asChild size="sm" variant="outline">
                  <Link to="/plans">{t("usage.changePlan")}</Link>
                </Button>
              </div>
            ) : null}
          </div>
        ) : null
      }
    </Region>
  )
}

function Usage({ current }: { current: CurrentSubscription }) {
  const { t, i18n } = useTranslation("overview")
  const locale = localeOf(i18n.language)
  const { usage, plan, subscription } = current
  const end = subscription?.currentPeriodEnd

  const bar = (label: string, used: number, limit: number, pct: number) => {
    const unlimited = limit < 0
    return (
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-2 text-sm">
          <span className="font-medium">{label}</span>
          <span className="numeric text-muted-foreground">
            {unlimited
              ? `${t("usage.usedUnlimited", { used: formatCount(used, locale) })} · ${t("usage.unlimited")}`
              : t("usage.used", {
                  used: formatCount(used, locale),
                  limit: formatCount(limit, locale),
                })}
          </span>
        </div>
        <ProgressBar value={unlimited ? null : pct} label={label} />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">
          {t("usage.plan", { name: plan.displayName ?? plan.name })}
        </span>
        {end ? (
          <>
            {" · "}
            {t(subscription.autoRenew === false ? "usage.ends" : "usage.renews", {
              date: formatDate(end, locale),
            })}
          </>
        ) : null}
      </p>
      {bar(t("usage.today"), usage.processedSmsToday, usage.dailyLimit, usage.dailyUsagePercentage)}
      {bar(
        t("usage.month"),
        usage.processedSmsLastMonth,
        usage.monthlyLimit,
        usage.monthlyUsagePercentage,
      )}
    </div>
  )
}
